/**
 * Mahnwesen. Die Mahnleiter ist Konfiguration der Agentur (DunningPolicy, WS-15):
 * Stufen mit Schlüssel, Frist (Werk- oder Kalendertage), Gebühr in Cent und Zinsschalter.
 *
 * Zinsen (WS-16): offener Bruttobetrag × (Basiszins + Aufschlag) × Tage / 365 ab der
 * Fälligkeit laut Beleg (document_due_date), taggenau über die Basiszins-Perioden, am Ende
 * einmal kaufmännisch gerundet.
 */
import {
  awaitsDelivery,
  confirmationActive,
  inCollection,
  isApproved,
  isCancelled,
  isLive,
  isPaid,
  isSent,
  originalDueDate,
  currentDueDate,
} from "./invoice-facts.js";
import { normalizeCountry } from "./countries.js";
import { convertWithFrozenRate, divRound } from "./money.js";
import { addDays, addDaysByUnit, diffDays } from "./periods.js";
import {
  DUNNING_COLLECTION,
  DUNNING_NONE,
  DomainError,
  type Cents,
  type Currency,
  type DunningPolicy,
  type DunningStageConfig,
  type Finding,
  type InvoiceRecord,
  type IsoDate,
} from "./types.js";

// ---------------------------------------------------------------------------
// Stufen
// ---------------------------------------------------------------------------

export function stageIndex(policy: DunningPolicy, key: string): number {
  return policy.stages.findIndex((s) => s.key === key);
}

export function getStage(policy: DunningPolicy, key: string): DunningStageConfig | undefined {
  return policy.stages.find((s) => s.key === key);
}

export function finalStage(policy: DunningPolicy): DunningStageConfig | undefined {
  return policy.stages[policy.stages.length - 1];
}

/**
 * Namen der Stufen mit Produkttext, wie die Mahnleiter der Agentur sie zeigt (Einstellungen · Mahnleiter). Probelauf N4: Das
 * Mahnschreiben zur Stufe final_notice hieß „3. Mahnung“, die Mahnleiter „Letzte Mahnung“.
 */
export const DUNNING_STAGE_NAMES: Readonly<Record<string, { de: string; en: string }>> = {
  reminder: { de: "Zahlungserinnerung", en: "Payment reminder" },
  first_notice: { de: "1. Mahnung", en: "1st dunning notice" },
  second_notice: { de: "2. Mahnung", en: "2nd dunning notice" },
  final_notice: { de: "Letzte Mahnung", en: "Final dunning notice" },
};

/**
 * Name einer Stufe, überall gleich (Mahnseite, Rechnung, Liste, Verlauf, Mahnschreiben): die eigene Bezeichnung aus der
 * Mahnleiter (label, 0109), sonst Stufen mit Produkttext wie in der Mahnleiter (DUNNING_STAGE_NAMES), eigene
 * Erinnerungsstufen „Zahlungserinnerung“, übrige eigene Stufen zählen ab 1 („1. Mahnung“, „2. Mahnung“), auch wenn die Leiter
 * ohne Erinnerung beginnt.
 */
export function dunningStageLabel(policy: DunningPolicy, key: string | null | undefined, lang: "de" | "en" = "de"): string {
  const en = lang === "en";
  if (!key || key === DUNNING_NONE) return en ? "none" : "keine";
  if (key === DUNNING_COLLECTION) return en ? "Debt collection" : "Inkasso";
  const i = stageIndex(policy, key);
  if (i < 0) return DUNNING_STAGE_NAMES[key]?.[lang] ?? key;
  const own = policy.stages[i]!.label?.trim();
  if (own) return own;
  const named = DUNNING_STAGE_NAMES[key];
  if (named) return named[lang];
  if (policy.stages[i]!.isReminder) return en ? "Payment reminder" : "Zahlungserinnerung";
  const n = policy.stages.slice(0, i + 1).filter((s) => !s.isReminder).length;
  if (!en) return `${n}. Mahnung`;
  const ord = n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`;
  return `${ord} dunning notice`;
}

/**
 * Gezählter Name einer Stufe („Zahlungserinnerung“, „1. Mahnung“, „2. Mahnung“ …) unabhängig von Bezeichnung und Schlüssel:
 * für das Einlesen fremder Bestände, deren Spalte „Mahnstufe“ gezählt ist (Probelauf N4: der sichtbare Name ist seitdem der
 * aus der Mahnleiter, etwa „Letzte Mahnung“).
 */
export function dunningStageOrdinalLabel(policy: DunningPolicy, key: string, lang: "de" | "en" = "de"): string {
  const en = lang === "en";
  const i = stageIndex(policy, key);
  if (i < 0) return key;
  if (policy.stages[i]!.isReminder) return en ? "Payment reminder" : "Zahlungserinnerung";
  const n = policy.stages.slice(0, i + 1).filter((s) => !s.isReminder).length;
  if (!en) return `${n}. Mahnung`;
  const ord = n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`;
  return `${ord} dunning notice`;
}

export function isFinalStage(policy: DunningPolicy, key: string): boolean {
  return policy.stages.length > 0 && finalStage(policy)?.key === key;
}

/**
 * MA-01: nächste Stufe. Ohne Stufe → erste Stufe; nach der letzten gibt es keine weitere
 * (null) – die Übergabe ins Inkasso ist eine eigene Handlung.
 * `skipReminder`: nach Fälligkeit wird eine reine Erinnerungsstufe übersprungen, wenn gewünscht.
 */
export function nextStage(policy: DunningPolicy, currentKey: string, opts: { skipReminder?: boolean } = {}): DunningStageConfig | null {
  if (currentKey === DUNNING_COLLECTION) return null;
  let idx = currentKey === DUNNING_NONE ? 0 : stageIndex(policy, currentKey) + 1;
  if (currentKey !== DUNNING_NONE && stageIndex(policy, currentKey) < 0) {
    throw new DomainError("UNKNOWN_DUNNING_STAGE", currentKey);
  }
  while (opts.skipReminder && policy.stages[idx]?.isReminder) idx += 1;
  return policy.stages[idx] ?? null;
}

// ---------------------------------------------------------------------------
// Fristen (MA-04, MA-05, WS-15)
// ---------------------------------------------------------------------------

/** Neue Frist eines Mahnschreibens: heute + Stufenfrist; von Hand überschreibbar. */
export function dunningDeadlineFor(
  stage: DunningStageConfig,
  today: IsoDate,
  opts: { override?: IsoDate | null; holidays?: IsoDate[]; dueDate?: IsoDate | null } = {},
): IsoDate {
  if (opts.override) return opts.override;
  // Zahlungserinnerung vor der Fälligkeit: Frist = Fälligkeit (MA-07). K-410.6: Ist die Rechnung heute fällig, gilt die Frist
  // der Stufe; eine neue Frist liegt immer nach heute (vorher „Frist heute, 0 Tage“)
  if (stage.isReminder && opts.dueDate && opts.dueDate > today) return opts.dueDate;
  const d = addDaysByUnit(today, stage.deadline.days, stage.deadline.unit, opts.holidays ?? []);
  return d > today ? d : addDaysByUnit(today, 1, stage.deadline.unit, opts.holidays ?? []);
}

/** MA-05: Ende der laufenden Mahnfrist = gespeicherte Frist, sonst letzte Mahnung + Stufenfrist. */
export function dunningDeadlineEnd(inv: InvoiceRecord, policy: DunningPolicy, holidays: IsoDate[] = []): IsoDate | null {
  if (inv.dunningDeadline) return inv.dunningDeadline;
  const stage = getStage(policy, inv.dunningStage);
  if (!stage || !inv.lastDunningAt) return null;
  return addDaysByUnit(inv.lastDunningAt, stage.deadline.days, stage.deadline.unit, holidays);
}

// ---------------------------------------------------------------------------
// Zinsen und Gebühren (MA-02, MA-03, WS-16)
// ---------------------------------------------------------------------------

function ratesSorted(policy: DunningPolicy) {
  return [...policy.baseRates].sort((a, b) => (a.validFrom < b.validFrom ? -1 : 1));
}

/** Basiszins an einem Tag (letzte Periode mit validFrom ≤ Tag). */
export function baseRateOn(policy: DunningPolicy, day: IsoDate): number {
  let r: number | null = null;
  for (const p of ratesSorted(policy)) if (p.validFrom <= day) r = p.percent;
  if (r === null) throw new DomainError("NO_BASE_RATE", day);
  return r;
}

export interface InterestResult {
  days: number;
  interestCents: Cents;
  /** Zinssatz des letzten Tags (Anzeige) */
  ratePercent: number;
  /**
   * Lag die Fälligkeit vor dem ersten gepflegten Basiszins, gilt für die Tage davor der früheste
   * bekannte Satz (Altforderung beim Start einer Agentur). Die App zeigt das als Hinweis (R-04).
   */
  baseRateAssumedUntil: IsoDate | null;
  segments: { from: IsoDate; to: IsoDate; days: number; ratePercent: number }[];
}

/**
 * Verzugszinsen taggenau. Zinstage = Tage nach der Fälligkeit bis einschließlich `until`;
 * ein 29. Februar zählt als Tag, der Divisor bleibt 365.
 */
export function computeInterest(input: {
  openGrossCents: Cents;
  dueDate: IsoDate;
  until: IsoDate;
  policy: DunningPolicy;
}): InterestResult {
  const days = Math.max(0, diffDays(input.dueDate, input.until));
  if (days === 0 || input.openGrossCents <= 0) {
    return { days, interestCents: 0, ratePercent: 0, segments: [], baseRateAssumedUntil: null };
  }
  const first = addDays(input.dueDate, 1);
  const rates = ratesSorted(input.policy);
  const earliest = rates[0];
  if (!earliest) throw new DomainError("NO_BASE_RATE", first);
  const assumed = first < earliest.validFrom ? addDays(earliest.validFrom, -1) : null;
  const rateOn = (day: IsoDate) => (day < earliest.validFrom ? earliest.percent : baseRateOn(input.policy, day));
  const segments: InterestResult["segments"] = [];
  let cursor = first;
  while (cursor <= input.until) {
    const rate = rateOn(cursor) + input.policy.surchargePercentPoints;
    const nextChange = rates.find((r) => r.validFrom > cursor)?.validFrom;
    const segEnd = nextChange && nextChange <= input.until ? addDays(nextChange, -1) : input.until;
    const segDays = diffDays(cursor, segEnd) + 1;
    segments.push({ from: cursor, to: segEnd, days: segDays, ratePercent: rate });
    cursor = addDays(segEnd, 1);
  }
  // Σ Betrag × Satz × Tage / 365 exakt: Satz in 1/10_000 %.
  let num = 0n;
  for (const s of segments) num += BigInt(input.openGrossCents) * BigInt(Math.round(s.ratePercent * 10_000)) * BigInt(s.days);
  const interest = Number(divRound(num, 365n * 100n * 10_000n));
  return { days, interestCents: interest, ratePercent: segments[segments.length - 1]?.ratePercent ?? 0, segments, baseRateAssumedUntil: assumed };
}

/**
 * Praxistest K-139: Verzugszinsen bis `until` mit Teilzahlungen. Bis zu jeder Zahlung (bzw. Ausbuchung) auf den damals offenen
 * Betrag, danach auf den Rest, taggenau wie interestUntilPaid. Eine Rückbuchung (kind reversal oder negativer Betrag) erhöht den
 * offenen Betrag wieder; Buchungen nach `until` zählen nicht. Vorher rechnete die Mahnung den heutigen Rest über die ganze Zeit.
 */
export function computeInterestWithPayments(input: {
  grossCents: Cents;
  dueDate: IsoDate;
  until: IsoDate;
  payments: readonly { paidOn: IsoDate; amountCents: Cents; reversal?: boolean }[];
  policy: DunningPolicy;
}): InterestResult {
  let open = input.grossCents;
  let cursor = input.dueDate;
  let total = 0;
  let rate = 0;
  let assumed: IsoDate | null = null;
  const segments: InterestResult["segments"] = [];
  const add = (to: IsoDate) => {
    if (open <= 0 || to <= cursor) return;
    const r = computeInterest({ openGrossCents: open, dueDate: cursor, until: to, policy: input.policy });
    total += r.interestCents;
    segments.push(...r.segments);
    if (r.ratePercent) rate = r.ratePercent;
    assumed ??= r.baseRateAssumedUntil;
  };
  const pays = input.payments
    .filter((p) => p.amountCents !== 0 && p.paidOn <= input.until)
    .map((p) => ({ paidOn: p.paidOn, amount: Math.abs(p.amountCents), back: !!p.reversal || p.amountCents < 0 }))
    .sort((a, b) => a.paidOn.localeCompare(b.paidOn));
  for (const p of pays) {
    add(p.paidOn);
    if (p.paidOn > cursor) cursor = p.paidOn;
    open += p.back ? p.amount : -p.amount;
  }
  add(input.until);
  return { days: Math.max(0, diffDays(input.dueDate, input.until)), interestCents: total, ratePercent: rate, segments, baseRateAssumedUntil: assumed };
}

/**
 * 0198: Verzugszinsen bis zur Zahlung für den Beleg über Verzugskosten. Taggenau je Abschnitt: Jede Zahlung (oder Ausbuchung)
 * senkt den offenen Betrag ab ihrem Datum, eine Rückbuchung erhöht ihn wieder. Zinsen nur auf den Teil nach der Fälligkeit.
 */
export function interestUntilPaid(input: {
  grossCents: Cents;
  dueDate: IsoDate;
  payments: readonly { paidOn: IsoDate; amountCents: Cents; reversal?: boolean }[];
  policy: DunningPolicy;
}): { interestCents: Cents; days: number; ratePercent: number; until: IsoDate | null } {
  let open = input.grossCents;
  let cursor = input.dueDate;
  let total = 0;
  let days = 0;
  let rate = 0;
  let until: IsoDate | null = null;
  const pays = [...input.payments].filter((p) => p.amountCents > 0).sort((a, b) => a.paidOn.localeCompare(b.paidOn));
  for (const p of pays) {
    if (open > 0 && p.paidOn > cursor) {
      const r = computeInterest({ openGrossCents: open, dueDate: cursor, until: p.paidOn, policy: input.policy });
      total += r.interestCents;
      days += r.days;
      if (r.ratePercent) rate = r.ratePercent;
      until = p.paidOn;
    }
    // ohne offenen Betrag laufen keine Zinsen: der nächste Abschnitt beginnt frühestens mit dieser Buchung (Rückbuchung)
    if (p.paidOn > cursor) cursor = p.paidOn;
    open += p.reversal ? p.amountCents : -p.amountCents;
  }
  return { interestCents: total, days, ratePercent: rate, until };
}

/**
 * MA-02: Gebühr bis einschließlich Zielstufe. 'highest' = einmalig die höchste Gebühr bis
 * zu dieser Stufe (nicht kumuliert), 'cumulative' = Summe aller Stufengebühren.
 * Die einmalige Pauschale (`flatFeeCents`) gilt, sobald bis zur Zielstufe eine Stufe erreicht ist, die keine
 * Erinnerung ist; die Stufengebühren werden auf sie angerechnet (höherer Betrag). Rückgabe in der Policy-Währung.
 */
export function feeUpToStage(policy: DunningPolicy, stageKey: string, sentStageKeys?: readonly string[]): Cents {
  const idx = stageIndex(policy, stageKey);
  if (idx < 0) throw new DomainError("UNKNOWN_DUNNING_STAGE", stageKey);
  // Mit Protokoll (0043): nur Stufen, die für diese Rechnung wirklich versandt oder nachgetragen wurden, plus die neue
  const reached = sentStageKeys
    ? policy.stages.filter((s) => s.key === stageKey || sentStageKeys.includes(s.key))
    : policy.stages.slice(0, idx + 1);
  const fees = reached.map((s) => s.feeCents);
  const stageFee = policy.feeMode === "cumulative" ? fees.reduce((a, b) => a + b, 0) : Math.max(0, ...fees);
  const flat = policy.flatFeeCents ?? 0;
  // Die Pauschale wird auf Mahnkosten angerechnet (§ 288 Abs. 5 S. 3 BGB, § 458 UGB): der höhere Betrag, nicht die Summe
  return flat > 0 && reached.some((s) => !s.isReminder) ? Math.max(stageFee, flat) : stageFee;
}

export interface DunningLine {
  kind: "principal" | "fee" | "interest";
  amountCents: Cents;
  /** Schlüssel des Rechtshinweises je Zeile (Texte je Land als Einstellung, MA-19) */
  noticeKey: string | null;
  params?: Record<string, string | number>;
}

/**
 * SAAS-3: gesetzlicher Verzugszins gegenüber Verbrauchern je Land der Agentur. Die Mahnleiter (Aufschlag je Land, Pauschale)
 * gilt für Geschäfte zwischen Unternehmen; gegenüber Privatpersonen gelten diese Sätze, und eine Verzugspauschale gibt es nicht
 * (DE § 288 Abs. 5 BGB, AT § 458 UGB nur unter Unternehmern). Ohne Eintrag berechnet das Mahnschreiben keine Zinsen.
 */
export type ConsumerInterestRule =
  | { mode: "base_plus"; surchargePercentPoints: number; basis: string }
  | { mode: "fixed"; percent: number; basis: string };
export const CONSUMER_INTEREST: Readonly<Record<string, ConsumerInterestRule>> = {
  DE: { mode: "base_plus", surchargePercentPoints: 5, basis: "§ 288 Abs. 1 BGB" },
  AT: { mode: "fixed", percent: 4, basis: "§ 1000 Abs. 1 ABGB" },
  CH: { mode: "fixed", percent: 5, basis: "Art. 104 Abs. 1 OR" },
};
export function consumerInterestRule(country: string | null | undefined): ConsumerInterestRule | null {
  const c = normalizeCountry(country ?? null);
  return c ? CONSUMER_INTEREST[c] ?? null : null;
}

/**
 * Hinweise zur Berechnung: Zinsen entfallen, weil ein Satz fehlt (PRAXIS-1, SAAS-3); CREDITOR_JURISDICTION_DIFFERS (0181): der
 * Gläubiger (bei der Mahnung im Auftrag der Creator) sitzt in einem anderen Land als das Konto, dessen Regeln die Mahnleiter trägt.
 * Dann rechnet CreatorOffice keine gesetzlichen Posten (Pauschale, Verzugszinsen) automatisch; die Gebühren der Stufen bleiben.
 */
export type DunningCostWarning = "NO_BASE_RATE" | "CONSUMER_RATE_UNKNOWN" | "CREDITOR_JURISDICTION_DIFFERS" | "INTEREST_FROM_REMINDER" | "NOT_YET_DUE"
  /** L14: keine vertraglich vereinbarte Zahlungsfrist und noch kein Verzug: diese Mahnung trägt weder Pauschale noch Zinsen */
  | "NO_AGREED_TERM";

/**
 * Steuerprüfung 23.10.2026, L14: Verzug ohne Mahnung gibt es nur bei vereinbarter, kalendermäßig bestimmter Leistungszeit
 * (§ 286 Abs. 2 Nr. 1 und 2 BGB); eine einseitig in der Rechnung gesetzte Frist genügt nicht (BGH, Urteil vom 25.10.2007,
 * III ZR 91/07). Ohne Vereinbarung beginnt der Verzug mit der ersten Mahnung nach Fälligkeit (§ 286 Abs. 1 BGB) oder 30 Tage nach
 * Fälligkeit und Zugang (§ 286 Abs. 3 BGB), je nachdem, was früher liegt. Vorsichtig gelesen: die 30 Tage zählen ab dem späteren
 * von Fälligkeit laut Beleg und Zugang; ohne bekannten Zugang nur die Mahnung; gegenüber Verbrauchern nur die Mahnung (der
 * 30-Tage-Verzug setzt dort einen besonderen Hinweis auf der Rechnung voraus, § 286 Abs. 3 Satz 1 Halbsatz 2 BGB).
 * Rückgabe: letzter Tag ohne Verzug (Zinsen ab dem Folgetag, wie computeInterest); null = noch kein Verzug feststellbar.
 */
export function defaultAnchorWithoutAgreedTerm(x: {
  due: IsoDate | null; receivedOn: IsoDate | null; dunningDates: readonly IsoDate[]; consumer?: boolean;
}): IsoDate | null {
  const dates = x.dunningDates.map((d) => d.slice(0, 10)).filter((d) => !x.due || d > x.due).sort();
  const reminder = dates[0] ?? null;
  const received = x.receivedOn?.slice(0, 10) ?? null;
  const base = received && x.due ? (received > x.due ? received : x.due) : received;
  const thirty = !x.consumer && base ? addDays(base, 30) : null;
  return [reminder, thirty].filter((d): d is IsoDate => !!d).sort()[0] ?? null;
}

/**
 * L14: Frist aus einem unterschriebenen Vertrag mit Zahlungsklausel (Vertragsbaukasten 0313 § 5: „Rechnungen sind innerhalb von
 * N Tagen nach Zugang … zu zahlen“, englisch „within N days of receipt“; Agenturgebühr „N Tage nach Zugang der Rechnung“).
 * Mehrere Verträge: die längste Frist (vorsichtig). Null = keine Klausel gefunden.
 */
export function agreedPaymentTermDays(contractBodies: readonly (string | null | undefined)[]): number | null {
  let best: number | null = null;
  for (const body of contractBodies) {
    const text = (body ?? "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ");
    // Livegang 04.11.2026: auch die Musterverträge mit „innerhalb von N Tagen ohne Abzug zu zahlen“ (Auftrag Marke an Agentur),
    // „innerhalb von N Tagen netto nach Rechnungseingang“ und den englischen Fassungen „payable without deduction within N days“,
    // „due within N days net after receipt“
    const re = /innerhalb von (\d{1,3}) Tagen nach Zugang|(\d{1,3}) Tage nach Zugang der Rechnung|within (\d{1,3}) days of receipt|(\d{1,3}) days after receipt of the invoice|innerhalb von (\d{1,3}) Tagen ohne Abzug zu zahlen|innerhalb von (\d{1,3}) Tagen netto nach Rechnungseingang|payable without deduction within (\d{1,3}) days|due within (\d{1,3}) days net after receipt/gi;
    for (const m of text.matchAll(re)) {
      const days = Number(m.slice(1).find((x) => x !== undefined));
      if (Number.isFinite(days) && (best === null || days > best)) best = days;
    }
  }
  return best;
}

/**
 * L14: Ist die Zahlungsfrist dieser Rechnung vertraglich vereinbart? Ja, wenn ein unterschriebener Vertrag eine Zahlungsklausel hat
 * und die Rechnung dem Kunden mindestens so viel Zeit lässt (sonst wäre die Fälligkeit laut Beleg früher als vereinbart).
 */
export function paymentTermAgreed(x: { contractDays: number | null; invoiceTermDays?: number | null; issueDate?: IsoDate | null; dueDate?: IsoDate | null }): boolean {
  if (x.contractDays === null) return false;
  const term = typeof x.invoiceTermDays === "number" ? x.invoiceTermDays
    : x.issueDate && x.dueDate ? diffDays(x.issueDate.slice(0, 10), x.dueDate.slice(0, 10)) : null;
  return term !== null && term >= x.contractDays;
}

/**
 * Rechtsordnung der automatisch berechneten Mahnkosten (0181). Die Mahnleiter trägt die Regeln des Sitzlands des Kontos
 * (platform_interest_rules: Aufschlag bzw. fester Satz, Basiszins; Pauschale als Einstellung des Kontos). Gilt für die Forderung
 * ein anderes Land (der Creator als Gläubiger sitzt anderswo), werden die Regeln nicht vermischt: keine automatische Pauschale,
 * keine automatischen Verzugszinsen. Unbekanntes Land des Gläubigers zählt als Land des Kontos (wie bisher).
 */
export function dunningStatutoryAutomatic(ruleCountry: string | null | undefined, creditorCountry: string | null | undefined): boolean {
  const rule = normalizeCountry(ruleCountry ?? null);
  const creditor = normalizeCountry(creditorCountry ?? null);
  return !rule || !creditor || rule === creditor;
}

/**
 * CH-2 (K-321, A1): Zinsregel einer Forderung: gegenüber Privatpersonen der Verbrauchersatz des Landes, sonst ein vertraglich
 * vereinbarter Satz bzw. die Regel des Landes. Null = für Verbraucher ist kein Satz hinterlegt.
 */
export function effectiveInterestPolicy(policy: DunningPolicy, o: { consumer: boolean; creditorCountry?: string | null }): DunningPolicy | null {
  if (o.consumer) {
    const rule = consumerInterestRule(o.creditorCountry);
    if (!rule) return null;
    return rule.mode === "fixed"
      ? { ...policy, baseRates: [{ validFrom: "1900-01-01", percent: rule.percent }], surchargePercentPoints: 0 }
      : { ...policy, surchargePercentPoints: rule.surchargePercentPoints };
  }
  if (policy.contractRatePercent != null && policy.contractRatePercent >= 0) {
    return { ...policy, baseRates: [{ validFrom: "1900-01-01", percent: policy.contractRatePercent }], surchargePercentPoints: 0 };
  }
  return policy;
}

export interface DunningCosts {
  /** Warum Zinsen trotz Zinsstufe entfallen (fehlender Basiszins, unbekannter Verbrauchersatz) */
  warnings?: DunningCostWarning[];
  /** Gegenüber einer Privatperson gerechnet (ohne Pauschale, Verbraucherzins) */
  consumer?: boolean;
  stageKey: string;
  currency: Currency;
  principalCents: Cents;
  feeCents: Cents;
  interestCents: Cents;
  interest: InterestResult | null;
  totalCents: Cents;
  /**
   * Worauf die Gebühr beruht (Prüfung 07.10.2026, M-1): „flat“ = die gesetzliche Verzugspauschale ist der höhere Betrag,
   * „stage_credited“ = Mahngebühren der Stufen, auf die die Pauschale angerechnet ist. Fehlt sie, sind es nur Stufengebühren.
   */
  feeBasis?: { kind: "flat" | "stage_credited"; law: string | null };
  lines: DunningLine[];
  /** Hinweise auf dem Schreiben: Verzug, Inkassoandrohung */
  noticeKeys: string[];
}

/**
 * Kosten und Positionen eines Mahnschreibens (MA-02, MA-03, MA-19).
 * `frozenRate` rechnet Gebühren in die Rechnungswährung um, wenn diese von der
 * Policy-Währung abweicht (Betrag in beiden Währungen zum Zeitpunkt des Einfrierens).
 */
export function computeDunningCosts(input: {
  invoice: Pick<InvoiceRecord, "amountGrossCents" | "currency" | "documentDueDate" | "issuerDueDate"> & { effectiveDueDate?: string | null };
  stageKey: string;
  today: IsoDate;
  policy: DunningPolicy;
  /** bereits gezahlter Teil (Teilzahlungen) */
  paidCents?: Cents;
  /**
   * Buchungen der Rechnung mit Datum (Zahlung, Ausbuchung, Rückbuchung). Mit Angabe laufen die Zinsen bis zu jeder Zahlung auf den
   * damals offenen Betrag (K-139); ohne Angabe auf den heutigen Rest.
   */
  payments?: readonly { paidOn: IsoDate; amountCents: Cents; reversal?: boolean }[];
  frozenRate?: { invoiceTotal: Cents; policyCurrencyTotal: Cents };
  /** Stufen, die laut Verlauf schon versandt oder nachgetragen wurden (Gebühr nur für diese plus die neue, wie die Datenbank) */
  sentStageKeys?: readonly string[];
  /** SAAS-3: Empfänger ist eine Privatperson (Verbraucher); dann Verbraucherzins des Landes und keine Pauschale */
  recipientConsumer?: boolean;
  /** Land der Agentur (Gläubiger) für den Verbraucherzins */
  creditorCountry?: string | null;
  /**
   * 0181: Land, dessen Regeln die Mahnleiter trägt (Sitzland des Kontos). Weicht creditorCountry davon ab, entfallen Pauschale
   * und Verzugszinsen (Warnung CREDITOR_JURISDICTION_DIFFERS); ohne Angabe wie bisher.
   */
  ruleCountry?: string | null;
  /** CH-2 (K-281): Tag der ersten Mahnung seit dem letzten Zurücksetzen (Zinsbeginn „reminder“) */
  firstReminderOn?: IsoDate | null;
  /**
   * L14: Zahlungsfrist vertraglich vereinbart (paymentTermAgreed). false: Verzug erst ab defaultAnchorWithoutAgreedTerm, davor
   * weder Pauschale noch Zinsen. Ohne Angabe wie bisher (Verzug ab Fälligkeit laut Beleg).
   */
  paymentTermAgreed?: boolean;
  /** L14: Zugang der Rechnung (Versand an die Marke) */
  receivedOn?: IsoDate | null;
  /** L14: Tage der bisherigen Mahnungen seit dem letzten Zurücksetzen */
  dunningDates?: readonly IsoDate[];
}): DunningCosts {
  const stage = getStage(input.policy, input.stageKey);
  if (!stage) throw new DomainError("UNKNOWN_DUNNING_STAGE", input.stageKey);
  const principal = input.invoice.amountGrossCents - (input.paidCents ?? 0);
  const consumer = !!input.recipientConsumer;
  const warnings: DunningCostWarning[] = [];
  // Verbraucher: keine Verzugspauschale; Zinsen nach dem Verbrauchersatz des Landes (fester Satz braucht keinen Basiszins)
  let policy: DunningPolicy = consumer ? { ...input.policy, flatFeeCents: 0 } : input.policy;
  let interestPossible = true;
  // 0181: Rechtsordnungen nicht vermischen (Gläubiger in einem anderen Land als das Konto): keine gesetzlichen Posten automatisch
  const mixed = input.ruleCountry !== undefined && !dunningStatutoryAutomatic(input.ruleCountry, input.creditorCountry);
  if (mixed) {
    policy = { ...policy, flatFeeCents: 0 };
    interestPossible = false;
    if (stage.interest || (input.policy.flatFeeCents ?? 0) > 0) warnings.push("CREDITOR_JURISDICTION_DIFFERS");
  }
  if (consumer && stage.interest && interestPossible) {
    const rule = consumerInterestRule(input.creditorCountry);
    if (!rule) { interestPossible = false; warnings.push("CONSUMER_RATE_UNKNOWN"); }
    else if (rule.mode === "fixed") policy = { ...policy, baseRates: [{ validFrom: "1900-01-01", percent: rule.percent }], surchargePercentPoints: 0 };
    else policy = { ...policy, surchargePercentPoints: rule.surchargePercentPoints };
  }
  // CH-2 (A1): vertraglich vereinbarter fester Satz zwischen Unternehmen statt Basiszins plus Aufschlag
  if (!consumer && input.policy.contractRatePercent != null && input.policy.contractRatePercent >= 0) {
    policy = { ...policy, baseRates: [{ validFrom: "1900-01-01", percent: input.policy.contractRatePercent }], surchargePercentPoints: 0 };
  }
  // PRAXIS-1: ohne gepflegten Basiszins entfallen die Zinsen dieser Mahnung (mit Hinweis), statt dass alles scheitert
  if (stage.interest && interestPossible && policy.baseRates.length === 0) { interestPossible = false; warnings.push("NO_BASE_RATE"); }
  // K-324/K-394d: vor der Fälligkeit besteht kein Verzug, also weder Pauschale (§ 288 Abs. 5 BGB, § 458 UGB) noch Mahngebühren
  // (wie app.dunning_fee_for)
  const dueForFee = input.invoice.effectiveDueDate ?? input.invoice.documentDueDate ?? input.invoice.issuerDueDate;
  const notYetDue = !!dueForFee && input.today <= dueForFee;
  if (notYetDue) {
    if (!stage.isReminder && ((policy.flatFeeCents ?? 0) > 0 || feeUpToStage({ ...policy, flatFeeCents: 0 }, stage.key, input.sentStageKeys) > 0)) warnings.push("NOT_YET_DUE");
    policy = { ...policy, flatFeeCents: 0 };
  }
  // L14: ohne vereinbarte Zahlungsfrist erst ab dem Verzugsbeginn Pauschale und Zinsen; der Zinsbeginn „ab Mahnung“ ist schon streng
  const noAgreedTerm = input.paymentTermAgreed === false && input.policy.interestStart !== "reminder";
  const defaultAnchor = noAgreedTerm
    // Schweiz und Liechtenstein kennen den 30-Tage-Verzug nicht (Art. 102 OR): dort wie bei Verbrauchern nur die Mahnung
    ? defaultAnchorWithoutAgreedTerm({ due: dueForFee ?? null, receivedOn: input.receivedOn ?? null, dunningDates: input.dunningDates ?? [],
        consumer: consumer || ["CH", "LI"].includes(normalizeCountry(input.creditorCountry ?? input.ruleCountry ?? null) ?? "") })
    : null;
  const notInDefault = noAgreedTerm && (!defaultAnchor || defaultAnchor >= input.today);
  if (notInDefault && !notYetDue) {
    if ((stage.interest && interestPossible) || (!stage.isReminder && (policy.flatFeeCents ?? 0) > 0)) warnings.push("NO_AGREED_TERM");
    policy = { ...policy, flatFeeCents: 0 };
  }
  if (notInDefault) interestPossible = false;
  let fee = notYetDue ? 0 : feeUpToStage(policy, stage.key, input.sentStageKeys);
  const stageOnly = feeUpToStage({ ...policy, flatFeeCents: 0 }, stage.key, input.sentStageKeys);
  const flat = policy.flatFeeCents ?? 0;
  const feeBasis: DunningCosts["feeBasis"] = flat > 0 && fee > stageOnly ? { kind: "flat", law: input.creditorCountry ?? null }
    : flat > 0 && stageOnly > 0 && fee === stageOnly && stageOnly >= flat && !stage.isReminder ? { kind: "stage_credited", law: input.creditorCountry ?? null } : undefined;
  if (fee > 0 && input.invoice.currency !== input.policy.feeCurrency) {
    if (!input.frozenRate) throw new DomainError("FROZEN_RATE_REQUIRED");
    fee = convertWithFrozenRate(fee, { fromTotal: input.frozenRate.policyCurrencyTotal, toTotal: input.frozenRate.invoiceTotal });
  }
  // K-318: nach einer vereinbarten Fristverlängerung beginnt der Verzug erst mit der neuen Frist (wie app.dunning_interest_cap)
  const due = input.invoice.effectiveDueDate ?? input.invoice.documentDueDate ?? input.invoice.issuerDueDate;
  // K-281: Zinsbeginn „reminder“ (Schweiz ohne Verfalltag): Verzug erst mit der ersten Mahnung; diese Mahnung selbst trägt keine Zinsen
  const interestStart = input.policy.interestStart === "reminder" ? (input.firstReminderOn ?? null) : due;
  const interestFrom = defaultAnchor && interestStart && defaultAnchor > interestStart ? defaultAnchor : interestStart;
  if (stage.interest && interestPossible && input.policy.interestStart === "reminder" && !interestFrom) {
    interestPossible = false;
    warnings.push("INTEREST_FROM_REMINDER");
  }
  let interest: InterestResult | null = null;
  if (stage.interest && interestPossible && interestFrom) {
    if (!due) throw new DomainError("DUE_DATE_REQUIRED");
    interest = input.payments?.length
      ? computeInterestWithPayments({ grossCents: input.invoice.amountGrossCents, dueDate: interestFrom, until: input.today, payments: input.payments, policy })
      : computeInterest({ openGrossCents: principal, dueDate: interestFrom, until: input.today, policy });
  }
  const lines: DunningLine[] = [{ kind: "principal", amountCents: principal, noticeKey: null, params: due ? { dueDate: due } : {} }];
  if (fee > 0) lines.push({ kind: "fee", amountCents: fee, noticeKey: "DUNNING_FEE" });
  if (interest && interest.interestCents > 0) {
    lines.push({
      kind: "interest",
      amountCents: interest.interestCents,
      noticeKey: "DUNNING_INTEREST",
      params: { ratePercent: interest.ratePercent, days: interest.days },
    });
  }
  const noticeKeys: string[] = [];
  if (!stage.isReminder) noticeKeys.push("DUNNING_DEFAULT_NOTICE");
  if (isFinalStage(input.policy, stage.key)) noticeKeys.push("DUNNING_COLLECTION_THREAT");
  const interestCents = interest?.interestCents ?? 0;
  return {
    ...(warnings.length ? { warnings } : {}),
    ...(consumer ? { consumer } : {}),
    stageKey: stage.key,
    currency: input.invoice.currency,
    principalCents: principal,
    feeCents: fee,
    interestCents,
    interest,
    totalCents: principal + fee + interestCents,
    ...(feeBasis && fee > 0 ? { feeBasis } : {}),
    lines,
    noticeKeys,
  };
}

// ---------------------------------------------------------------------------
// Darf gemahnt werden? (MA-06, MA-07, MA-08, MA-14, MA-16, WS-17)
// ---------------------------------------------------------------------------

export interface DunningContext {
  today: IsoDate;
  policy: DunningPolicy;
  /** Die Marke hat den Leistungsmonat (bzw. Deal) bereits bezahlt – aus den Monatsdaten */
  brandPaidForPeriod?: boolean;
  holidays?: IsoDate[];
}

export interface DunningCheck {
  allowed: boolean;
  /** erster Sperrgrund */
  blockedReason: string | null;
  nextStage: DunningStageConfig | null;
  /** nicht blockierende Rückfragen, die der Nutzer bestätigen muss */
  confirmations: Finding[];
}

/** Sperrgrund für Mahnungen, oder null. Reihenfolge = Priorität der Anzeige. */
export function dunningBlockReason(inv: InvoiceRecord, ctx: DunningContext): string | null {
  if (!isLive(inv) || inv.status === "void" || inv.status === "deleted") return "INVOICE_VOID";
  if (!isApproved(inv)) return "NOT_APPROVED";
  if (isCancelled(inv)) return "CANCELLED";
  if (isPaid(inv)) return "PAID";
  if (!isSent(inv)) return "NOT_SENT";
  // 0055: ohne Zugang der Rechnung beginnt keine Frist und kein Verzug
  if (awaitsDelivery(inv)) return "NOT_DELIVERED";
  if (inv.recipientIsOwnOrg) return "RECIPIENT_IS_OWN_ORG";
  if (inCollection(inv)) return "IN_COLLECTION";
  if (inv.dunningBlocked) return "DUNNING_BLOCKED";
  if (ctx.brandPaidForPeriod) return "BRAND_ALREADY_PAID";
  if (confirmationActive(inv)) return "PAYMENT_CONFIRMED_BY_AGENCY";
  return null;
}

/**
 * Nächste Mahnung prüfen. Vor der Fälligkeit nur mit Rückfrage – außer bei einer
 * Erinnerungsstufe (MA-07). Hat das Talent „bezahlt“ gemeldet, nur mit Rückfrage (MA-06).
 */
export function checkDunning(inv: InvoiceRecord, ctx: DunningContext, opts: { repeatFinal?: boolean } = {}): DunningCheck {
  const reason = dunningBlockReason(inv, ctx);
  const due = currentDueDate(inv);
  const overdue = !!due && due < ctx.today;
  if (reason) return { allowed: false, blockedReason: reason, nextStage: null, confirmations: [] };
  // Stufe gibt es in der Mahnleiter nicht mehr (umbenannt oder gelöscht): sperren statt abstürzen (R-08)
  if (inv.dunningStage !== DUNNING_NONE && inv.dunningStage !== DUNNING_COLLECTION && !getStage(ctx.policy, inv.dunningStage)) {
    return { allowed: false, blockedReason: "UNKNOWN_STAGE", nextStage: null, confirmations: [] };
  }
  // AD-157: die letzte Stufe noch einmal senden (ausdrücklich gewählt, z. B. „Letzte Mahnung erneut senden“); ohne Option bleibt
  // nach der letzten Stufe nur die Übergabe ans Inkasso als nächster Schritt
  const next = nextStage(ctx.policy, inv.dunningStage)
    ?? (opts.repeatFinal && isFinalStage(ctx.policy, inv.dunningStage) ? getStage(ctx.policy, inv.dunningStage) ?? null : null);
  if (!next) return { allowed: false, blockedReason: "NO_FURTHER_STAGE", nextStage: null, confirmations: [] };
  const confirmations: Finding[] = [];
  if (!overdue && !next.isReminder) {
    confirmations.push({ code: "DUN_BEFORE_DUE", severity: "question", params: due ? { dueDate: due } : {} });
  }
  if (inv.talentPaymentResponse === "paid") confirmations.push({ code: "TALENT_REPORTED_PAID", severity: "question" });
  // Frist der letzten Mahnung läuft noch: nur mit Rückfrage (R-10)
  const end = dunningDeadlineEnd(inv, ctx.policy, ctx.holidays);
  if (inv.dunningStage !== DUNNING_NONE && end && ctx.today <= end) {
    confirmations.push({ code: "DUNNING_DEADLINE_RUNNING", severity: "question", params: { date: end } });
  }
  return { allowed: true, blockedReason: null, nextStage: next, confirmations };
}

/** Kurzform für die Zustandsableitung. */
export function canSendDunning(inv: InvoiceRecord, ctx: DunningContext): boolean {
  return checkDunning(inv, ctx).allowed;
}

// ---------------------------------------------------------------------------
// Inkasso (MA-09, MA-10, MA-13)
// ---------------------------------------------------------------------------

export interface CollectionCheck {
  allowed: boolean;
  blockedReason: string | null;
  confirmations: Finding[];
}

/** MA-09: Übergabe ins Inkasso nur nach letzter Stufe mit abgelaufener Frist. */
export function checkCollectionHandover(inv: InvoiceRecord, ctx: DunningContext): CollectionCheck {
  const no = (r: string): CollectionCheck => ({ allowed: false, blockedReason: r, confirmations: [] });
  if (!isLive(inv)) return no("INVOICE_VOID");
  if (isPaid(inv)) return no("PAID");
  if (isCancelled(inv)) return no("CANCELLED");
  if (inCollection(inv)) return no("IN_COLLECTION");
  if (inv.recipientIsOwnOrg) return no("RECIPIENT_IS_OWN_ORG");
  if (inv.dunningBlocked) return no("DUNNING_BLOCKED");
  if (!isApproved(inv) || !isSent(inv)) return no("NOT_SENT");  // Stufe von Hand gesetzt, Rechnung nie versendet (R-06)
  if (ctx.brandPaidForPeriod) return no("BRAND_ALREADY_PAID");
  if (confirmationActive(inv)) return no("PAYMENT_CONFIRMED_BY_AGENCY");  // wie bei der Mahnung (MA-16/RE-34, R-05)
  if (!isFinalStage(ctx.policy, inv.dunningStage)) return no("NOT_FINAL_STAGE");
  const end = dunningDeadlineEnd(inv, ctx.policy, ctx.holidays);
  if (end && ctx.today <= end) return no("DEADLINE_RUNNING");
  const confirmations: Finding[] = [];
  if (inv.talentPaymentResponse === "paid") confirmations.push({ code: "TALENT_REPORTED_PAID", severity: "question" });
  return { allowed: true, blockedReason: null, confirmations };
}

/** MA-13: Anzeige „Frist abgelaufen – Inkasso fällig“ ohne Zustandsänderung. */
export function isCollectionDue(inv: InvoiceRecord, ctx: DunningContext): boolean {
  const c = checkCollectionHandover(inv, ctx);
  return c.allowed;
}

/** MA-10: Deal auf „Inkasso“ stempelt nur die Rechnungen, die MA-09 erfüllen. */
export function partitionForCollection(
  invoices: readonly InvoiceRecord[],
  ctx: DunningContext,
): { eligible: InvoiceRecord[]; unchanged: { invoice: InvoiceRecord; reason: string }[] } {
  const eligible: InvoiceRecord[] = [];
  const unchanged: { invoice: InvoiceRecord; reason: string }[] = [];
  for (const inv of invoices) {
    if (!isApproved(inv) || isPaid(inv) || !isLive(inv)) continue;
    const c = checkCollectionHandover(inv, ctx);
    if (c.allowed) eligible.push(inv);
    else unchanged.push({ invoice: inv, reason: c.blockedReason ?? "UNKNOWN" });
  }
  return { eligible, unchanged };
}

/** Tage seit der Fälligkeit laut Beleg (RE-09). */
export function daysSinceOriginalDue(inv: InvoiceRecord, today: IsoDate): number {
  const d = originalDueDate(inv);
  return d ? Math.max(0, diffDays(d, today)) : 0;
}
